import React, {useCallback, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Link} from 'react-router-dom';
import createAxiosInstance from '../../support/AxiosSupport';
import Spinner from '../../components/Spinner';
import {AlertTriangleIcon, ChevronDownIcon, ChevronUpIcon} from '../../components/icons';

const CHATGPT_MODELS = [
    {value: 'gpt-5.6-sol',   label: 'GPT-5.6 Sol'},
    {value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra'},
    {value: 'gpt-5.6-luna',  label: 'GPT-5.6 Luna'},
    {value: 'gpt-5.4',       label: 'GPT-5.4'},
    {value: 'gpt-5.4-mini',  label: 'GPT-5.4 Mini'},
    {value: 'gpt-5.4-nano',  label: 'GPT-5.4 Nano'},
    {value: 'gpt-4o',        label: 'GPT-4o'},
    {value: 'gpt-4o-mini',   label: 'GPT-4o Mini'},
];
const DEFAULT_MODEL = 'gpt-5.6-terra';

type ByoProvider = 'OPENAI_KEY' | 'CLAUDE_CLI' | 'CODEX_CLI';
type AiSource = 'PLATFORM' | 'BYO';

interface UserSettingsDto {
    chatGptTokenConfigured: boolean;
    chatGptTokenLast5?: string;
    chatGptModel?: string;
    aiSourcePreference?: AiSource | null;
    platformAiAllowed?: boolean;
    effectiveAiSource?: AiSource | null;
    aiKillSwitchActive?: boolean;
    byoProvider?: ByoProvider | null;
    claudeCliConfigured?: boolean;
    claudeCliTokenLast4?: string | null;
    codexCliConfigured?: boolean;
    claudeCliAvailable?: boolean;
    codexCliAvailable?: boolean;
    // byo-model-select (FR-3): выбранные модели CLI и белые списки с сервера.
    claudeCliModel?: string | null;
    codexCliModel?: string | null;
    claudeCliModels?: string[];
    codexCliModels?: string[];
}

const BYO_PROVIDER_OPTIONS: Array<{value: ByoProvider; label: string; hint: string}> = [
    {value: 'OPENAI_KEY', label: 'API-ключ OpenAI', hint: 'платный API-ключ, тарификация за токены'},
    {value: 'CLAUDE_CLI', label: 'Подписка Claude (Claude Code CLI)', hint: 'оплаченная подписка Claude Pro/Max'},
    {value: 'CODEX_CLI', label: 'Подписка ChatGPT (Codex CLI)', hint: 'оплаченная подписка ChatGPT Plus/Pro'},
];

/** Классы радио-карточки (шаги 1–2 иерархии): выбранная / обычная / задизейбленная. */
const RADIO_CARD_BASE = 'flex items-start gap-3 p-3 rounded-lg border transition-colors';
const radioCardClass = (selected: boolean): string =>
    `${RADIO_CARD_BASE} cursor-pointer ${selected
        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`;
const RADIO_CARD_DISABLED = `${RADIO_CARD_BASE} opacity-60 cursor-not-allowed border-gray-200 dark:border-gray-700`;

/** Текст ошибки из тела ответа backend ({@code {error, code, description}}) с фолбэком. */
const errorText = (e: unknown, fallback: string): string => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = (e as any)?.response?.data;
    return data?.description || data?.error || fallback;
};

interface CliTestResult {
    ok: boolean;
    message: string;
}

type CliTestState =
    | {status: 'idle'}
    | {status: 'loading'}
    | {status: 'ok' | 'error'; message: string};

/**
 * Селект модели CLI-провайдера (byo-model-select, FR-9): «По умолчанию (подписка)» + белый
 * список с бэка; сохранённое значение вне списка помечается «(недоступна)» (EC-4).
 */
interface CliModelSelectProps {
    label: string;
    value: string | null | undefined;
    options: string[];
    disabled: boolean;
    onSave: (model: string | null) => Promise<void>;
}

const CliModelSelect: React.FC<CliModelSelectProps> = ({label, value, options, disabled, onSave}) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleChange = async (v: string) => {
        setSaving(true);
        setError(null);
        try {
            await onSave(v || null);
        } catch (e) {
            setError(errorText(e, 'Не удалось сохранить модель.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</span>
            <div className="flex gap-2 items-center flex-wrap">
                <select value={value || ''} onChange={e => handleChange(e.target.value)}
                        aria-label={label} disabled={disabled || saving} className="input-field max-w-xs">
                    <option value="">По умолчанию (подписка)</option>
                    {value && !options.includes(value) && (
                        <option value={value}>{value} (недоступна)</option>
                    )}
                    {options.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                {saving && <span className="text-sm text-gray-500 dark:text-gray-400">Сохранение...</span>}
            </div>
            {error && <p className="text-sm text-red-600 dark:text-red-400 mt-1" role="alert">{error}</p>}
        </div>
    );
};

interface CliProviderCardProps {
    title: string;
    subtitle: string;
    available: boolean;
    configured: boolean;
    /** Последние символы привязанного токена (только Claude). */
    tokenLast4?: string | null;
    instructionTitle: string;
    instructionSteps: Array<{text: string; code?: string}>;
    multiline: boolean;
    inputLabel: string;
    inputPlaceholder: string;
    confirmText: string;
    /** Модель провайдера (byo-model-select): селект показывается при привязанных кредах. */
    modelLabel: string;
    modelValue?: string | null;
    modelOptions?: string[];
    onSaveModel: (model: string | null) => Promise<void>;
    onSave: (value: string) => Promise<void>;
    onDetach: () => Promise<void>;
    onTest: () => Promise<CliTestResult>;
}

/**
 * Карточка привязки CLI-провайдера (Claude / Codex): статус, свёрнутая инструкция,
 * ввод креда, кнопки Сохранить/Проверить/Отвязать, селект модели (FR-2…FR-5, FR-8, byo-model-select).
 * Значение креда после сохранения очищается и никогда не отображается.
 */
const CliProviderCard: React.FC<CliProviderCardProps> = ({
    title, subtitle, available, configured, tokenLast4, instructionTitle, instructionSteps,
    multiline, inputLabel, inputPlaceholder, confirmText,
    modelLabel, modelValue, modelOptions, onSaveModel, onSave, onDetach, onTest,
}) => {
    const [value, setValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [test, setTest] = useState<CliTestState>({status: 'idle'});
    const [showInstructions, setShowInstructions] = useState(false);

    const disabled = !available || saving;

    const handleSave = async () => {
        if (!value.trim()) return;
        setSaving(true);
        setError(null);
        try {
            await onSave(value.trim());
            setValue('');
            setTest({status: 'idle'});
        } catch (e) {
            setError(errorText(e, 'Не удалось сохранить. Попробуйте ещё раз.'));
        } finally {
            setSaving(false);
        }
    };

    const handleDetach = async () => {
        if (!window.confirm(confirmText)) return;
        setSaving(true);
        setError(null);
        try {
            await onDetach();
            setTest({status: 'idle'});
        } catch (e) {
            setError(errorText(e, 'Не удалось отвязать. Попробуйте ещё раз.'));
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        setTest({status: 'loading'});
        try {
            const res = await onTest();
            setTest({status: res.ok ? 'ok' : 'error', message: res.message});
        } catch (e) {
            setTest({status: 'error', message: errorText(e, 'Не удалось выполнить проверку.')});
        }
    };

    return (
        <div className={`card p-6 ${!available ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h4>
                {!available && <span className="badge badge-warning">Недоступен на сервере</span>}
                {available && configured && (
                    <span className="badge badge-success">
                        Привязан{tokenLast4 ? ` ····${tokenLast4}` : ''}
                    </span>
                )}
                {available && !configured && <span className="badge badge-neutral">Не привязан</span>}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{subtitle}</p>

            <button type="button" disabled={!available}
                    onClick={() => setShowInstructions(v => !v)}
                    className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400
                               hover:text-gray-900 dark:hover:text-gray-200 mb-3 disabled:cursor-not-allowed">
                {showInstructions ? <ChevronUpIcon size={16}/> : <ChevronDownIcon size={16}/>}
                {instructionTitle}
            </button>
            {showInstructions && (
                <ol className="list-decimal list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1 mb-3">
                    {instructionSteps.map(step => (
                        <li key={step.text}>
                            {step.text}
                            {step.code && (
                                <code className="ml-1 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10 font-mono text-xs">
                                    {step.code}
                                </code>
                            )}
                        </li>
                    ))}
                </ol>
            )}

            <div className="space-y-3">
                {multiline ? (
                    <textarea value={value} onChange={e => setValue(e.target.value)} rows={4}
                              aria-label={inputLabel} placeholder={inputPlaceholder}
                              disabled={disabled} autoComplete="off"
                              className="input-field font-mono text-xs"/>
                ) : (
                    <input type="password" value={value} onChange={e => setValue(e.target.value)}
                           aria-label={inputLabel} placeholder={inputPlaceholder}
                           disabled={disabled} autoComplete="off"
                           className="input-field font-mono"/>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={handleSave} disabled={disabled || !value.trim()} className="btn-primary">
                        {saving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                    {configured && (
                        <button onClick={handleTest} disabled={disabled || test.status === 'loading'}
                                className="btn-secondary">
                            Проверить
                        </button>
                    )}
                    {configured && (
                        <button onClick={handleDetach} disabled={disabled}
                                className="inline-flex items-center justify-center py-2.5 px-4 rounded-xl text-sm font-medium
                                           text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10
                                           transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Отвязать
                        </button>
                    )}
                </div>
                {error && <p className="text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>}
                {test.status === 'loading' && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Проверка подключения...</p>
                )}
                {test.status === 'ok' && (
                    <p className="text-sm text-brand-700 dark:text-brand-300">{test.message}</p>
                )}
                {test.status === 'error' && (
                    <p className="text-sm text-red-600 dark:text-red-400" role="alert">{test.message}</p>
                )}
                {configured && available && (
                    <CliModelSelect label={modelLabel} value={modelValue}
                                    options={modelOptions || []} disabled={disabled}
                                    onSave={onSaveModel}/>
                )}
            </div>
        </div>
    );
};

const ProfileAiPage: React.FC = () => {
    const [settings, setSettings] = useState<UserSettingsDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [tokenInput, setTokenInput] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [tokenSaving, setTokenSaving] = useState(false);
    const [modelSaving, setModelSaving] = useState(false);
    const [aiSourceSaving, setAiSourceSaving] = useState(false);
    const [aiSourceError, setAiSourceError] = useState<string | null>(null);
    const [chatGptModel, setChatGptModel] = useState<string>(DEFAULT_MODEL);
    const [byoProviderSaving, setByoProviderSaving] = useState(false);
    const [byoProviderError, setByoProviderError] = useState<string | null>(null);

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<UserSettingsDto>('/profile/settings');
            setSettings(res.data);
            setChatGptModel(res.data?.chatGptModel || DEFAULT_MODEL);
        } catch {
            setSettings({chatGptTokenConfigured: false});
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchSettings(); }, [fetchSettings]);

    const saveToken = async () => {
        setTokenSaving(true);
        try {
            const api = await createAxiosInstance();
            await api.put('/profile/settings', {chatGptToken: tokenInput.trim() || null});
            setTokenInput('');
            setEditMode(false);
            fetchSettings();
        } finally {
            setTokenSaving(false);
        }
    };

    const deleteToken = async () => {
        setTokenSaving(true);
        try {
            const api = await createAxiosInstance();
            await api.put('/profile/settings', {chatGptToken: null});
            setEditMode(false);
            fetchSettings();
        } finally {
            setTokenSaving(false);
        }
    };

    const saveModel = async (v: string) => {
        setChatGptModel(v);
        setModelSaving(true);
        try {
            const api = await createAxiosInstance();
            await api.put('/profile/settings/model', {chatGptModel: v});
            fetchSettings();
        } finally {
            setModelSaving(false);
        }
    };

    const saveAiSource = async (v: AiSource | '') => {
        setAiSourceSaving(true);
        setAiSourceError(null);
        try {
            const api = await createAxiosInstance();
            await api.put('/profile/settings/ai-source', {aiSource: v || null});
            await fetchSettings();
        } catch (e) {
            setAiSourceError(errorText(e, 'Не удалось сохранить источник AI.'));
        } finally {
            setAiSourceSaving(false);
        }
    };

    const saveByoProvider = async (v: ByoProvider) => {
        setByoProviderSaving(true);
        setByoProviderError(null);
        try {
            const api = await createAxiosInstance();
            await api.put('/profile/settings/byo-provider', {byoProvider: v});
            await fetchSettings();
        } catch (e) {
            setByoProviderError(errorText(e, 'Не удалось сохранить выбор провайдера.'));
        } finally {
            setByoProviderSaving(false);
        }
    };

    const saveClaudeToken = async (token: string) => {
        const api = await createAxiosInstance();
        await api.put('/profile/settings/ai-cli/claude', {token});
        await fetchSettings();
    };

    const detachClaudeToken = async () => {
        const api = await createAxiosInstance();
        await api.put('/profile/settings/ai-cli/claude', {token: null});
        await fetchSettings();
    };

    const testClaude = async (): Promise<CliTestResult> => {
        const api = await createAxiosInstance();
        const res = await api.post<CliTestResult>('/profile/settings/ai-cli/claude/test');
        return res.data;
    };

    // byo-model-select (FR-4): смена модели CLI-провайдера.
    const saveClaudeModel = async (model: string | null) => {
        const api = await createAxiosInstance();
        await api.put('/profile/settings/ai-cli/claude/model', {model});
        await fetchSettings();
    };

    const saveCodexAuth = async (authJson: string) => {
        const api = await createAxiosInstance();
        await api.put('/profile/settings/ai-cli/codex', {authJson});
        await fetchSettings();
    };

    const detachCodexAuth = async () => {
        const api = await createAxiosInstance();
        await api.put('/profile/settings/ai-cli/codex', {authJson: null});
        await fetchSettings();
    };

    const testCodex = async (): Promise<CliTestResult> => {
        const api = await createAxiosInstance();
        const res = await api.post<CliTestResult>('/profile/settings/ai-cli/codex/test');
        return res.data;
    };

    const saveCodexModel = async (model: string | null) => {
        const api = await createAxiosInstance();
        await api.put('/profile/settings/ai-cli/codex/model', {model});
        await fetchSettings();
    };

    if (loading && !settings) return <Spinner/>;

    const aiSource: AiSource | '' = settings?.aiSourcePreference || '';
    const isByo = aiSource === 'BYO';
    const selectedByoProvider: ByoProvider = settings?.byoProvider || 'OPENAI_KEY';
    // Подписочные CLI выполняют чат и распознавание, но не умеют строить эмбеддинги: с ними
    // индексация гардероба и поиск дублей стоят. На стенде это молча ломало половину функций (R3/Q2).
    const cliSubscriptionSelected = selectedByoProvider === 'CLAUDE_CLI' || selectedByoProvider === 'CODEX_CLI';
    const selectedCliNotReady =
        (selectedByoProvider === 'CLAUDE_CLI' && !(settings?.claudeCliConfigured && settings?.claudeCliAvailable !== false)) ||
        (selectedByoProvider === 'CODEX_CLI' && !(settings?.codexCliConfigured && settings?.codexCliAvailable !== false));

    /** Мелкая сводка привязок невыбранных провайдеров (шаг 2): что уже настроено. */
    const otherConfigured: string[] = [];
    if (selectedByoProvider !== 'OPENAI_KEY' && settings?.chatGptTokenConfigured) {
        otherConfigured.push('API-ключ OpenAI');
    }
    if (selectedByoProvider !== 'CLAUDE_CLI' && settings?.claudeCliConfigured) {
        otherConfigured.push(`подписка Claude${settings?.claudeCliTokenLast4 ? ` ····${settings.claudeCliTokenLast4}` : ''}`);
    }
    if (selectedByoProvider !== 'CODEX_CLI' && settings?.codexCliConfigured) {
        otherConfigured.push('подписка ChatGPT (Codex)');
    }

    return (
        <div className="space-y-6">
            {settings?.aiKillSwitchActive && (
                <div className="card p-4 border-l-4 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        AI-распознавание временно отключено администратором. Вещи можно добавлять вручную.
                    </p>
                </div>
            )}

            {/* Шаг 1. Источник AI */}
            <div className="card p-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">Источник AI</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Каким AI выполнять функции приложения: платформенным (по тарифу) или своим —
                    личным API-ключом либо подпиской.
                </p>
                <fieldset disabled={aiSourceSaving || loading}>
                    <div className="flex flex-col gap-2">
                        <label className={radioCardClass(aiSource === '')}>
                            <input type="radio" name="aiSource" value="" checked={aiSource === ''}
                                   onChange={() => saveAiSource('')} className="mt-0.5 accent-primary-600"/>
                            <div>
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Автоматически</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                    — платформенный при доступном тарифе, иначе свой
                                </span>
                            </div>
                        </label>
                        <label className={settings?.platformAiAllowed
                            ? radioCardClass(aiSource === 'PLATFORM') : RADIO_CARD_DISABLED}>
                            <input type="radio" name="aiSource" value="PLATFORM" checked={aiSource === 'PLATFORM'}
                                   disabled={!settings?.platformAiAllowed}
                                   onChange={() => saveAiSource('PLATFORM')} className="mt-0.5 accent-primary-600"/>
                            <div>
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Платформенный AI</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                    — {settings?.platformAiAllowed ? 'входит в ваш тариф' : 'нужен тариф PRO/FAMILY'}
                                </span>
                            </div>
                        </label>
                        <label className={radioCardClass(isByo)}>
                            <input type="radio" name="aiSource" value="BYO" checked={isByo}
                                   onChange={() => saveAiSource('BYO')} className="mt-0.5 accent-primary-600"/>
                            <div>
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    Свой ключ или подписка (BYO)
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                    — личный API-ключ OpenAI либо подписка Claude/ChatGPT
                                </span>
                            </div>
                        </label>
                    </div>
                </fieldset>
                {aiSourceSaving && <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Сохранение...</p>}
                {aiSourceError && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-2" role="alert">{aiSourceError}</p>
                )}
                {settings?.effectiveAiSource && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Сейчас используется: {settings.effectiveAiSource === 'PLATFORM' ? 'платформенный AI' : 'ваш ключ/подписка'}.
                    </p>
                )}
            </div>

            {/* Шаг 2 и 3 — только для BYO: выбор личного провайдера и карточка выбранного */}
            {isByo && (
                <div className="space-y-4">
                    <div className="card p-6">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            Личный AI-провайдер
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Выберите, чем выполнять AI-функции: личным API-ключом OpenAI или уже
                            оплаченной подпиской через официальный CLI.
                        </p>
                        <fieldset disabled={byoProviderSaving || loading}>
                            <div className="flex flex-col gap-2">
                                {BYO_PROVIDER_OPTIONS.map(opt => (
                                    <label key={opt.value} className={radioCardClass(selectedByoProvider === opt.value)}>
                                        <input type="radio" name="byoProvider" value={opt.value}
                                               checked={selectedByoProvider === opt.value}
                                               onChange={() => saveByoProvider(opt.value)}
                                               className="mt-0.5 accent-primary-600"/>
                                        <div>
                                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                {opt.label}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                                — {opt.hint}
                                            </span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </fieldset>
                        {byoProviderSaving && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Сохранение...</p>
                        )}
                        {byoProviderError && (
                            <p className="text-sm text-red-600 dark:text-red-400 mt-2" role="alert">{byoProviderError}</p>
                        )}
                        {cliSubscriptionSelected && (
                            <div className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-500/15">
                                <AlertTriangleIcon size={16} className="mt-0.5 shrink-0 text-amber-700 dark:text-amber-300"/>
                                <div className="text-sm text-amber-700 dark:text-amber-300">
                                    <p>
                                        Индексация гардероба и поиск дублей используют эмбеддинги — нужен
                                        API-ключ OpenAI; подписки Claude/ChatGPT (CLI) их не строят.
                                        {settings?.chatGptTokenConfigured
                                            ? ' Ключ OpenAI у вас привязан — выберите его провайдером, чтобы очередь пошла.'
                                            : ' Пока ключа нет, очередь индексации будет стоять.'}
                                    </p>
                                    <Link
                                        to="/indexing"
                                        className="inline-flex min-h-[44px] items-center font-medium underline hover:no-underline"
                                    >
                                        Состояние индексации
                                    </Link>
                                </div>
                            </div>
                        )}
                        {otherConfigured.length > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                                Также привязано: {otherConfigured.join(', ')}.
                            </p>
                        )}
                        {selectedCliNotReady && (
                            <div className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-500/15">
                                <AlertTriangleIcon size={16} className="mt-0.5 shrink-0 text-amber-700 dark:text-amber-300"/>
                                <p className="text-sm text-amber-700 dark:text-amber-300">
                                    Выбранный провайдер не готов (креды не привязаны или CLI недоступен) —
                                    пока будет использован другой доступный источник AI.
                                </p>
                            </div>
                        )}
                    </div>

                    {selectedByoProvider === 'OPENAI_KEY' && (
                        <div className="card p-6">
                            <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    API-ключ OpenAI
                                </h4>
                                {settings?.chatGptTokenConfigured
                                    ? <span className="badge badge-success">Привязан</span>
                                    : <span className="badge badge-neutral">Не привязан</span>}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                Личный API-ключ OpenAI: AI-функции тарифицируются за токены на вашем аккаунте.
                            </p>
                            <div className="space-y-4">
                                {!settings?.chatGptTokenConfigured && !editMode && (
                                    <div className="flex gap-2">
                                        <input type="password" value={tokenInput} onChange={e => setTokenInput(e.target.value)}
                                               aria-label="API-ключ OpenAI" placeholder="sk-..."
                                               className="input-field flex-1 font-mono" autoComplete="off"/>
                                        <button onClick={saveToken} disabled={!tokenInput.trim() || tokenSaving} className="btn-primary">
                                            {tokenSaving ? 'Сохранение...' : 'Добавить'}
                                        </button>
                                    </div>
                                )}
                                {settings?.chatGptTokenConfigured && !editMode && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-gray-700 dark:text-gray-300">
                                            {'*'.repeat(15)}{settings.chatGptTokenLast5 || ''}
                                        </span>
                                        <button onClick={() => setEditMode(true)} className="btn-secondary text-sm">Изменить</button>
                                        <button onClick={deleteToken} disabled={tokenSaving} className="btn-danger text-sm">
                                            {tokenSaving ? '...' : 'Удалить'}
                                        </button>
                                    </div>
                                )}
                                {editMode && (
                                    <div className="flex gap-2">
                                        <input type="password" value={tokenInput} onChange={e => setTokenInput(e.target.value)}
                                               aria-label="Новый API-ключ OpenAI" placeholder="sk-..."
                                               className="input-field flex-1 font-mono" autoComplete="off"/>
                                        <button onClick={saveToken} disabled={!tokenInput.trim() || tokenSaving} className="btn-primary">
                                            {tokenSaving ? 'Сохранение...' : 'Сохранить'}
                                        </button>
                                        <button onClick={() => { setEditMode(false); setTokenInput(''); }} disabled={tokenSaving}
                                                className="btn-secondary">
                                            Отмена
                                        </button>
                                    </div>
                                )}
                                <div>
                                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Модель OpenAI (API-ключ)
                                    </span>
                                    <div className="flex gap-2 items-center">
                                        <select value={chatGptModel} onChange={e => saveModel(e.target.value)}
                                                aria-label="Модель OpenAI (API-ключ)"
                                                disabled={modelSaving} className="input-field max-w-xs">
                                            {chatGptModel && !CHATGPT_MODELS.some(m => m.value === chatGptModel) && (
                                                <option value={chatGptModel}>{chatGptModel} (устаревшая)</option>
                                            )}
                                            {CHATGPT_MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                        </select>
                                        {modelSaving && <span className="text-sm text-gray-500">Сохранение...</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedByoProvider === 'CLAUDE_CLI' && (
                        <CliProviderCard
                            title="Подписка Claude (Claude Code CLI)"
                            subtitle="Привяжите OAuth-токен подписки Claude — AI-функции будут работать через Claude Code CLI без API-ключа."
                            available={settings?.claudeCliAvailable !== false}
                            configured={!!settings?.claudeCliConfigured}
                            tokenLast4={settings?.claudeCliTokenLast4}
                            instructionTitle="Как получить токен"
                            instructionSteps={[
                                {text: 'Установите CLI на своём компьютере:', code: 'npm install -g @anthropic-ai/claude-code'},
                                {text: 'Выполните команду и войдите в аккаунт с подпиской:', code: 'claude setup-token'},
                                {text: 'Скопируйте выданный токен и вставьте его в поле ниже.'},
                            ]}
                            multiline={false}
                            inputLabel="Токен Claude"
                            inputPlaceholder="sk-ant-oat01-..."
                            confirmText="Отвязать подписку Claude? Токен будет удалён."
                            modelLabel="Модель Claude"
                            modelValue={settings?.claudeCliModel}
                            modelOptions={settings?.claudeCliModels}
                            onSaveModel={saveClaudeModel}
                            onSave={saveClaudeToken}
                            onDetach={detachClaudeToken}
                            onTest={testClaude}
                        />
                    )}

                    {selectedByoProvider === 'CODEX_CLI' && (
                        <CliProviderCard
                            title="Подписка ChatGPT (Codex CLI)"
                            subtitle="Привяжите креды Codex CLI — AI-функции будут работать через подписку ChatGPT без API-ключа."
                            available={settings?.codexCliAvailable !== false}
                            configured={!!settings?.codexCliConfigured}
                            instructionTitle="Как получить auth.json"
                            instructionSteps={[
                                {text: 'Установите CLI на своём компьютере:', code: 'npm install -g @openai/codex'},
                                {text: 'Выполните команду и войдите в аккаунт с подпиской:', code: 'codex login'},
                                {text: 'Вставьте в поле ниже содержимое файла', code: '~/.codex/auth.json'},
                            ]}
                            multiline={true}
                            inputLabel="Содержимое auth.json"
                            inputPlaceholder='{"OPENAI_API_KEY": null, "tokens": {...}}'
                            confirmText="Отвязать подписку ChatGPT? Сохранённый auth.json будет удалён."
                            modelLabel="Модель Codex"
                            modelValue={settings?.codexCliModel}
                            modelOptions={settings?.codexCliModels}
                            onSaveModel={saveCodexModel}
                            onSave={saveCodexAuth}
                            onDetach={detachCodexAuth}
                            onTest={testCodex}
                        />
                    )}

                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Использование личной подписки через официальный CLI — в зоне вашей ответственности:
                        убедитесь, что такой сценарий соответствует условиям использования провайдера.
                    </p>
                </div>
            )}
        </div>
    );
};

export default observer(ProfileAiPage);
